import React, { useState, useEffect, useRef } from "react";

import { Constants } from "@eagerdog/constants";
import { IEvent, IRunningOrderDogClass, IShow } from "@eagerdog/interfaces";

import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import StopWatch from "src/components/EnterResults/StopWatch/StopWatch";

import styles from "./index.module.scss";

export interface IScentworkSearchSettings {
  timeLimit?: string;
  numHides?: number;
}

export interface IEditRunningOrderDogClassValues {
  duration: number;
  // Single-search classes
  timeLimit?: string;
  numHides?: number;
  // Multi-search classes (Master all elements, Excellent Interior): index 0..2
  searches?: IScentworkSearchSettings[];
}

interface IProps {
  event: IEvent
  show: IShow
  dogClass?: IRunningOrderDogClass
  onSubmit: (values: IEditRunningOrderDogClassValues) => void
}

// Number of searches a scentwork class runs. Mirrors ScentworkResults so the
// settings dialog offers exactly the fields results entry will read.
export const getScentworkSearchCount = (dogClass?: IRunningOrderDogClass): number => {
  const level = dogClass?.level;
  const isMaster = level === Constants.dog_class_element_level.master ||
    level === Constants.dog_class_element_level.scentwork_master;
  const isExcellent = level === Constants.dog_class_element_level.excellent ||
    level === Constants.dog_class_element_level.scentwork_excellent;
  const isInterior = dogClass?.show_element === Constants.dog_class_element.scentwork_interior;

  if (isMaster && isInterior) return 3;
  if (isExcellent && isInterior) return 2;
  return 1;
};

const readSearches = (dogClass?: IRunningOrderDogClass): IScentworkSearchSettings[] => [
  { timeLimit: dogClass?.time_limit_search_1 ?? "", numHides: dogClass?.num_hides_search_1 ?? 0 },
  { timeLimit: dogClass?.time_limit_search_2 ?? "", numHides: dogClass?.num_hides_search_2 ?? 0 },
  { timeLimit: dogClass?.time_limit_search_3 ?? "", numHides: dogClass?.num_hides_search_3 ?? 0 },
];

const EditRunningOrderDogClass = ({ event, show, dogClass, onSubmit }: IProps) => {
  const isScentwork = event.sanctioning_club === Constants.sanctioning_club.AKC &&
    show.show_type === Constants.show_type.scentwork;
  const searchCount = isScentwork ? getScentworkSearchCount(dogClass) : 1;
  const isMultiSearch = searchCount > 1;

  // Single-search classes. Older Master tiles for 1-search elements were saved
  // under the search_1 fields, so fall back to those for display.
  const [timeLimit, setLimitTime] = useState<string>(dogClass?.time_limit ?? dogClass?.time_limit_search_1 ?? "");
  const [numberOfHides, setNumberOfHides] = useState<number>(dogClass?.num_hides ?? dogClass?.num_hides_search_1 ?? 0);

  // Multi-search classes
  const [searches, setSearches] = useState<IScentworkSearchSettings[]>(() => readSearches(dogClass));

  // Use ref for duration to avoid cursor jumping when typing
  const durationRef = useRef<number>(dogClass?.duration ?? 0);

  // Reset state when dogClass changes (e.g., when editing a different class)
  useEffect(() => {
    durationRef.current = dogClass?.duration ?? 0;
    setLimitTime(dogClass?.time_limit ?? dogClass?.time_limit_search_1 ?? "");
    setNumberOfHides(dogClass?.num_hides ?? dogClass?.num_hides_search_1 ?? 0);
    setSearches(readSearches(dogClass));
  }, [dogClass]);

  const updateSearch = (index: number, patch: IScentworkSearchSettings) => {
    setSearches((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const handleSubmit = () => {
    const duration = durationRef.current;

    if (isMultiSearch) {
      onSubmit({ duration, searches: searches.slice(0, searchCount) });
    } else {
      onSubmit({ duration, timeLimit, numHides: numberOfHides });
    }
  }

  const classKey = `${dogClass?.show_element}-${dogClass?.level}-${dogClass?.sex_split}-${dogClass?.variation}`;

  return (
    <div className={styles.EditRunningOrderDogClass}>
      <div className={styles.title}>
        Edit Dog Class
      </div>
      <div className={styles.actions}>
        <Input
          key={`duration-${classKey}`}
          required
          type="number"
          step="any"
          defaultValue={(dogClass?.duration ?? 0).toString()}
          label="Duration (minutes)"
          placeholder="Please enter maximum run duration in minutes"
          onChange={(e) => {
            const value = e.target.value;
            // Don't update if just a decimal point (allow typing)
            if (value === '' || value === '.') {
              return;
            }
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
              durationRef.current = parsed;
            }
          }}
        />

        {isScentwork && !isMultiSearch && (
          <>
            <StopWatch
              label="Time Limit"
              time={timeLimit}
              hideTT
              onChange={(value: string) => {
                setLimitTime(value);
              }}
            />
            <Input
              key={`numHides-${classKey}`}
              label="Number of Hides"
              type="number"
              min={0}
              max={20}
              defaultValue={numberOfHides.toString()}
              onChange={(e) => {
                setNumberOfHides(Number(e.target.value));
              }}
            />
          </>
        )}

        {isScentwork && isMultiSearch && searches.slice(0, searchCount).map((search, index) => (
          <React.Fragment key={`search-${index}-${classKey}`}>
            <div style={{ marginTop: '20px', fontWeight: 'bold', fontSize: '14px' }}>Search {index + 1}</div>
            <StopWatch
              label={`Time Limit (Search ${index + 1})`}
              time={search.timeLimit ?? ""}
              hideTT
              onChange={(value: string) => {
                updateSearch(index, { timeLimit: value });
              }}
            />
            <Input
              key={`numHides${index + 1}-${classKey}`}
              label={`Number of Hides (Search ${index + 1})`}
              type="number"
              min={0}
              max={20}
              defaultValue={(search.numHides ?? 0).toString()}
              onChange={(e) => {
                updateSearch(index, { numHides: Number(e.target.value) });
              }}
            />
          </React.Fragment>
        ))}

        <Button type="submit" onClick={handleSubmit}>Save</Button>
      </div>
    </div>
  );
}

export { EditRunningOrderDogClass }
